import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTheme } from 'next-themes';
import { Moon, Sun, Search, Terminal, Menu, X } from 'lucide-react';
import SearchModal from './SearchModal';

export default function Navbar() {
  const [searchOpen, setSearchOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [mounted, setMounted] = useState(false);
  const { theme, setTheme } = useTheme();

  useEffect(() => setMounted(true), []);

  useEffect(() => {
    const handler = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setSearchOpen(s => !s);
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, []);

  const toggleTheme = () => setTheme(theme === 'dark' ? 'light' : 'dark');

  return (
    <>
      <nav className="sticky top-0 z-40 w-full border-b border-border bg-background/90 backdrop-blur-sm">
        <div className="max-w-screen-xl mx-auto px-6 md:px-12 h-16 flex items-center justify-between">
          {/* Logo */}
          <Link
            to="/"
            data-testid="nav-logo"
            className="flex items-center gap-2 font-extrabold text-xl hover:opacity-80 transition-opacity"
            style={{ fontFamily: 'Manrope, sans-serif' }}
          >
            <Terminal className="h-5 w-5" />
            <span>DevBlog</span>
          </Link>

          {/* Desktop Nav */}
          <div className="hidden md:flex items-center gap-5">
            <Link
              to="/"
              data-testid="nav-home"
              className="text-sm font-medium text-muted-foreground hover:text-foreground transition-colors"
            >
              Home
            </Link>
            <button
              data-testid="nav-search-btn"
              onClick={() => setSearchOpen(true)}
              className="flex items-center gap-2 text-sm font-medium text-muted-foreground hover:text-foreground transition-colors"
            >
              <Search className="h-4 w-4" />
              <span className="hidden lg:inline">Search</span>
              <kbd className="hidden lg:inline text-xs bg-muted border border-border px-1.5 py-0.5 rounded font-mono">
                ⌘K
              </kbd>
            </button>
            <button
              data-testid="theme-toggle-btn"
              onClick={toggleTheme}
              className="text-muted-foreground hover:text-foreground transition-colors"
              aria-label="Toggle theme"
            >
              {mounted && theme === 'dark' ? (
                <Sun className="h-4 w-4" />
              ) : (
                <Moon className="h-4 w-4" />
              )}
            </button>
            <Link
              to="/"
              className="hover:opacity-80 transition-opacity"
              aria-label="VP Engineer"
            >
              <img 
                src="/images/vp-engineer-logo.png" 
                alt="VP Engineer" 
                className="h-8 w-auto dark:invert"
              />
            </Link>
          </div>

          {/* Mobile toggle */}
          <div className="md:hidden flex items-center gap-3">
            <Link
              to="/"
              className="hover:opacity-80 transition-opacity"
              aria-label="VP Engineer"
            >
              <img 
                src="/images/vp-engineer-logo.png" 
                alt="VP Engineer" 
                className="h-6 w-auto dark:invert"
              />
            </Link>
            <button
              className="text-muted-foreground hover:text-foreground transition-colors"
              onClick={() => setMobileOpen(s => !s)}
              data-testid="mobile-menu-btn"
              aria-label="Toggle menu"
            >
              {mobileOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileOpen && (
          <div className="md:hidden border-t border-border bg-background px-6 py-4 space-y-4 animate-in">
            <Link to="/" onClick={() => setMobileOpen(false)} className="block text-sm font-medium hover:text-primary transition-colors">
              Home
            </Link>
            <button
              onClick={() => { setSearchOpen(true); setMobileOpen(false); }}
              className="flex items-center gap-2 text-sm font-medium text-muted-foreground hover:text-foreground transition-colors"
            >
              <Search className="h-4 w-4" /> Search
            </button>
            <button
              onClick={toggleTheme}
              className="flex items-center gap-2 text-sm font-medium text-muted-foreground hover:text-foreground transition-colors"
            >
              {mounted && theme === 'dark' ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
              {mounted && theme === 'dark' ? 'Light Mode' : 'Dark Mode'}
            </button>
          </div>
        )}
      </nav>
      <SearchModal open={searchOpen} onClose={() => setSearchOpen(false)} />
    </>
  );
}
